import React, { Suspense, useState, useEffect, useMemo, useRef, useCallback } from "react";
import { notification, Skeleton, ConfigProvider, Alert, Button, Form } from "antd";
import dayjs from "dayjs";
import { DirectDebitRunPayment } from "../../../model/payments/DirectDebitRunPayment";
import { useMemberService } from "../../../services/members/MemberServiceProvider";
import { UpdateMemberRequest } from "../../../requests/member/UpdateMemberRequest";
import { CreateMemberRequest } from "../../../requests/member/CreateMemberRequest";
import { SelfServiceCreateMemberRequest } from "../../../requests/member/SelfServiceCreateMemberRequest";
import { toDateOnly } from "../../../utils/DateOnly";
import MemberSearchBar from "../../common/MemberSearchBar";
import { MemberProfile } from "../../../model/members/MemberProfile";
import { MemberCancellation } from "../../../model/members/MemberCancellation";
import { HealthFormSummary } from "../../../model/members/HealthFormSummary";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { PointOfSaleTransaction } from "../../../model/pointofsale/PointOfSaleTransaction";
import { usePointOfSaleService } from "../../../services/pointofsale/PointOfSaleServiceProvider";
import { usePaymentService } from "../../../services/payments/PaymentServiceProvider";
import { MembershipCategory } from "../../../model/members/MembershipCategory";
import { MemberMetadataTypeDescriptor } from "../../../model/members/MemberMetadataTypeDescriptor";
import { useMember } from "../../../common/member/MemberContext";
import { forceTokenRefresh } from "../../../common/auth/tokenRefreshCoordinator";

import LayoutTwoColumn from "./v2/LayoutTwoColumn";
import CreateMemberLayout from "./v2/CreateMemberLayout";
import ProfileCompletionBanner from "./v2/ProfileCompletionBanner";
import CompleteProfileWizard, { CompleteProfilePayload } from "./v2/CompleteProfileWizard";
import { deriveMemberProfileMode, ProfileEntryMode } from "./v2/modeConfig";
import { computeAlerts, screenStatus } from "./v2/alerts";
import { DATE_OF_BIRTH_NOT_PROVIDED, computeProfileCompletion, isDirectDebitComplete, isMandateSelfEditable } from "./v2/completion";
import { fortiusProfileTheme } from "./v2/fortiusTheme";
import { DEFAULT_HEALTH_SCHEMA } from "../forms/defaultHealthSchema";
import { parseSchema, schemaToAnswers } from "../forms/schemaToAnswers";
import { computeFlaggedAnswers, isHealthQuestionnairePopulated } from "../forms/healthFlags";
import { HealthAnswer } from "../../../model/members/HealthFormSummary";
import { parseBirthdayMessage, birthdayGreeting } from "./birthdayMessage";
import { parseBannerRules } from "./bannerRules";
import { useClientConfig } from "../../../common/client/ClientConfigProvider";
import "./v2/fortius-profile.css";

interface MemberProfileScreenV2Props {
  selfServiceMode?: boolean;
  // Create/onboarding entry. 'onboarding' is the self-service blank create
  // (also inferrable from ?onboarding=1 on /my-profile); 'adminCreate' is the
  // admin/FoH blank create on /members/new. Defaults to the normal view.
  entry?: ProfileEntryMode;
}

// A blank profile to seed the create/onboarding form. Dates default to today so
// the (locked, server-owned) validity fields render sensibly; the server owns
// validFrom / validTo / status / isEnabled on create.
const buildBlankMember = (emailAddress: string): MemberProfile => ({
  name: "",
  username: emailAddress,
  dateOfBirth: undefined as unknown as Date,
  emailAddress,
  addressLine1: "",
  addressLine2: "",
  townCity: "",
  countyState: "",
  postZipCode: "",
  phoneNumber: "",
  membershipId: "",
  specialOffers: false,
  status: "Inactive",
  memberType: "Member",
  membershipCategory: undefined as unknown as MembershipCategory,
  allPayments: [],
  photoUrl: "",
  validFrom: new Date(),
  validTo: new Date(),
  metadata: {},
  auth0Id: "",
  isEnabled: false,
  country: "",
});

const MemberProfileScreenV2: React.FC<MemberProfileScreenV2Props> = ({ selfServiceMode = false, entry = "view" }) => {
  const { permissions, validateAuth, isLoading: permissionsLoading } = useMember();
  const { getAccessTokenSilently } = useAuth0();
  const memberService = useMemberService();
  const pointOfSaleService = usePointOfSaleService();
  const paymentService = usePaymentService();
  const { membershipId } = useParams<{ membershipId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [member, setMember] = useState<MemberProfile>();
  const [memberPayments, setMemberPayments] = useState<DirectDebitRunPayment[]>([]);
  const [pointOfSaleTransactions, setPointOfSaleTransactions] = useState<PointOfSaleTransaction[]>([]);
  const [membershipCategories, setMembershipCategories] = useState<MembershipCategory[]>([]);
  const [metadataTypes, setMetadataTypes] = useState<MemberMetadataTypeDescriptor[]>([]);
  const [cancellations, setCancellations] = useState<MemberCancellation[]>([]);
  const [healthSummary, setHealthSummary] = useState<HealthFormSummary | null>(null);
  // Starts true so the Health tab warning (below) never flashes a "no
  // questionnaire" state in the frame between the member loading and the summary
  // fetch starting. `healthLoadError` records a failed fetch so we don't turn a
  // transient API error into a false "no questionnaire completed" claim.
  const [healthLoading, setHealthLoading] = useState(true);
  const [healthLoadError, setHealthLoadError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchBarKey, setSearchBarKey] = useState(0);
  const [checkInTime, setCheckInTime] = useState<Date | null>(null);
  const [checkingIn, setCheckingIn] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  // Self-service profile load failed (e.g. a transient 403 while the fresh token
  // is still propagating). We surface a retry instead of bouncing to /home, which
  // — combined with the completion redirect that pins members to /my-profile —
  // would ping-pong and hammer /members/me/profile unthrottled.
  const [selfLoadFailed, setSelfLoadFailed] = useState(false);
  const selfLoadAttemptedRef = useRef(false);
  // The health questionnaire is rendered inline at the bottom of the create page
  // (not a modal/tab). On Save its answers are collected from this form and ride
  // along on the create request — mandatory for self-service onboarding, optional
  // for admin/front-of-house. `creatingMember` gates the Save button while the
  // create request is in flight so it can't be double-submitted.
  const [creatingMember, setCreatingMember] = useState(false);
  // Synchronous re-entry guard for the create request: `creatingMember` (state)
  // only disables the Save button on the next render, which leaves a window during
  // the async health validation where a second click could fire another create.
  const creatingRef = useRef(false);
  const [healthForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState("tx");
  const [checkinRefreshKey, setCheckinRefreshKey] = useState(0);
  const hasCheckedInRef = useRef(false);
  // Self-service only: whether the member's direct-debit mandate is settled — i.e.
  // either real details are stored, or they're partial and only the club can fix
  // them (the server rejects a member self-update unless all four fields are empty).
  // `null` = not established yet (still loading, or the fetch failed), which keeps
  // the completion banner from claiming a missing mandate off incomplete data.
  const [mandateSettled, setMandateSettled] = useState<boolean | null>(null);
  const [completionWizardOpen, setCompletionWizardOpen] = useState(false);
  // The member closed the completion prompt for this visit. Not persisted: it's a
  // nudge, so it returns next time rather than being permanently silenced (a member
  // who deferred direct debit shouldn't be stuck behind a banner they can't clear).
  const [completionDismissed, setCompletionDismissed] = useState(false);

  // Whether the signed-in user actually has a member (auth/sync = the DB truth).
  // This — not the token's membership_id or the URL — decides onboarding vs profile.
  const hasLinkedMember = !!permissions?.member?.membershipId;

  // Effective entry mode. An explicit prop wins (admin /members/new →
  // 'adminCreate'). In self-service, MEMBER EXISTENCE is the source of truth:
  // an existing member — even a partial one — always loads their real profile, so
  // ?onboarding=1 (a stale link, or lingering during the create round-trip) never
  // overwrites it with a blank create form. We onboard only when the signed-in user
  // genuinely has no member; while auth/sync is still resolving we hold on a
  // skeleton rather than guess. This is robust to a direct /my-profile refresh and
  // to a stale token membership_id.
  const effectiveEntry: ProfileEntryMode = useMemo(() => {
    if (entry !== "view") return entry;
    if (!selfServiceMode) return "view";
    if (hasLinkedMember) return "view";        // member exists → load/populate their profile
    if (permissionsLoading) return "view";     // still resolving → wait (screen shows a skeleton)
    return "onboarding";                        // resolved, no member → onboarding create
  }, [entry, selfServiceMode, hasLinkedMember, permissionsLoading]);

  const isCreating = effectiveEntry !== "view";

  // One mode config drives what is shown/editable for admin / front-desk /
  // self-service. Derived once from selfServiceMode + the current user's role +
  // the entry mode (onboarding / adminCreate unlock create fields).
  const mode = useMemo(
    () => deriveMemberProfileMode(selfServiceMode, permissions, effectiveEntry),
    [selfServiceMode, permissions, effectiveEntry],
  );

  // Health tab: the client's configured health schema drives both rendering and
  // flag detection, falling back to the bundled PARQ when none is configured.
  const clientSettings = useClientConfig();
  // Client-specific direct-debit cancellation terms. Empty => no notice/agreement
  // is shown when setting up a mandate.
  const cancellationNotice = (clientSettings.directDebit?.cancellationNotice ?? "").trim();
  const healthSchema = useMemo(() => {
    const parsed = parseSchema(clientSettings.forms?.healthJson);
    return parsed.sections.length > 0 ? parsed : DEFAULT_HEALTH_SCHEMA;
  }, [clientSettings.forms?.healthJson]);

  // Client-configurable profile banner rules (e.g. "membership expires in N
  // days"); fold into the Needs-attention alerts.
  const bannerRules = useMemo(
    () => parseBannerRules(clientSettings.banners?.rulesJson),
    [clientSettings.banners?.rulesJson],
  );

  // Birthday greeting on check-in, driven by checkin.birthdayMessage config.
  const birthdayConfig = useMemo(
    () => parseBirthdayMessage(clientSettings.checkin?.birthdayMessageJson),
    [clientSettings.checkin?.birthdayMessageJson],
  );

  // Fetch membership categories on mount.
  useEffect(() => {
    const fetchMembershipCategories = async () => {
      try {
        const categories = (await memberService.getMembershipCategories()) ?? [];
        // Staff (admin / front-desk) may select any category, including hidden ones
        // (e.g. the "Default New Member" placeholder). Members only ever see the
        // visible categories in self-service / onboarding.
        setMembershipCategories(selfServiceMode ? categories.filter((c) => c.isVisible) : categories);
      } catch (error) {
        console.error("Error fetching membership categories:", error);
        notification.error({ message: "Error", description: "Failed to load membership categories" });
      }
    };
    fetchMembershipCategories();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Staff-only: load the metadata-type catalogue for the generic Metadata section.
  // Skipped in self-service (the endpoint requires CanReadAllMemberData, and the
  // section is hidden there anyway).
  useEffect(() => {
    if (selfServiceMode) return;
    const fetchMetadataTypes = async () => {
      try {
        setMetadataTypes((await memberService.getMetadataTypes()) ?? []);
      } catch (error) {
        console.error("Error fetching metadata types:", error);
      }
    };
    fetchMetadataTypes();
  }, [selfServiceMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Create/onboarding entry — skip all loads and seed a blank profile. When the
  // email is server-owned and locked (onboarding self-service) we prefill it from
  // the signed-in Auth0 user — that user IS the new member. But when the email is
  // editable (admin/kiosk "Create User"), the signed-in user is staff or a shared
  // tablet account, so we start it BLANK: staff must enter the member's email
  // explicitly, otherwise they could silently create the member with the
  // admin/kiosk address. Everything else starts empty to fill in.
  useEffect(() => {
    if (!isCreating) return;
    const seedEmail = mode.editEmail ? "" : (permissions?.authUser?.emailAddress ?? "");
    setMember(buildBlankMember(seedEmail));
    setLoading(false);
  }, [isCreating, mode.editEmail, permissions?.authUser?.emailAddress]);

  // No membership id (admin landing) → drop the initial loading state so the
  // search bar shows.
  useEffect(() => {
    if (!membershipId && !selfServiceMode && !isCreating) {
      setLoading(false);
    }
  }, [membershipId, selfServiceMode, isCreating]);

  // Auto check-in via ?checkin=true.
  const shouldCheckIn = useMemo(() => {
    if (selfServiceMode) return false;
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get("checkin") === "true";
  }, [location.search, selfServiceMode]);

  // Shared self-service profile loader, used by both the mount effect and the
  // retry button so the two paths can't drift. getMyProfile resolves to undefined
  // on an API error (the base client swallows it when showError is off), so a 403
  // takes the empty branch rather than throw — both branches raise selfLoadFailed.
  // It must NEVER navigate away on failure: the completionRequired pin in App
  // would bounce us straight back and re-trigger the load in an unthrottled loop.
  const runSelfLoad = useCallback(async () => {
    setLoading(true);
    try {
      const memberProfile = await memberService.getMyProfile();
      if (memberProfile && memberProfile.name !== "") {
        setMember(memberProfile);
        setSelfLoadFailed(false);
      } else {
        notification.error({ message: "Error", description: "Unable to load your profile" });
        setSelfLoadFailed(true);
      }
    } catch (error) {
      console.error("Error loading own profile:", error);
      notification.error({ message: "Error", description: "Failed to load your profile" });
      setSelfLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [memberService]);

  // Load own profile in self-service mode. Only once auth/sync has resolved and the
  // user actually has a member — a no-member user is routed to onboarding above, so
  // we never call getMyProfile for them (it would return an empty profile off a
  // stale token and bounce them home in a loop). Attempt at most once per mount;
  // a failure surfaces a retry instead.
  useEffect(() => {
    if (!selfServiceMode || isCreating || permissionsLoading || !hasLinkedMember) return;
    if (selfLoadAttemptedRef.current) return;
    selfLoadAttemptedRef.current = true;
    runSelfLoad();
  }, [selfServiceMode, isCreating, permissionsLoading, hasLinkedMember, runSelfLoad]);

  // Explicit retry for a failed self-service load (e.g. once the fresh token has
  // propagated). Re-runs the same loader directly — independent of the mount guard.
  const retrySelfLoad = useCallback(() => {
    setSelfLoadFailed(false);
    return runSelfLoad();
  }, [runSelfLoad]);

  // Re-read the member's own profile WITHOUT flipping the screen into its loading
  // skeleton or its load-failed state. Used after the completion wizard saves: the
  // profile is already on screen, so a spinner would just flash, and a failed
  // refresh must not replace a perfectly good profile with an error card.
  const refreshMyProfileQuietly = useCallback(async () => {
    try {
      const memberProfile = await memberService.getMyProfile();
      if (!memberProfile || memberProfile.name === "") return;
      // /profile carries no photo (it's fetched separately), and the per-member
      // effect won't re-run because the membershipId is unchanged — so carry the
      // loaded photo over or the avatar blanks until a reload.
      setMember((prev) => ({ ...memberProfile, photoUrl: memberProfile.photoUrl || prev?.photoUrl || "" }));
    } catch (error) {
      console.error("Error refreshing own profile:", error);
    }
  }, [memberService]);

  // Load member by ID (admin mode).
  useEffect(() => {
    if (selfServiceMode || isCreating) return;
    const loadMember = async () => {
      if (membershipId) {
        setLoading(true);
        try {
          let memberProfile: MemberProfile | undefined;
          try {
            memberProfile = await memberService.getMember(membershipId, false);
          } catch (error) {
            console.log("Member not found by internal ID, trying Legacy ID lookup...");
          }
          if (!memberProfile || memberProfile.name === "") {
            try {
              memberProfile = await memberService.getByLegacyId(membershipId);
            } catch (error) {
              console.log("Member not found by Legacy ID either");
            }
          }
          if (memberProfile && memberProfile.name !== "") {
            if (member && member.photoUrl && !memberProfile.photoUrl) {
              memberProfile.photoUrl = member.photoUrl;
            }
            setMember(memberProfile);
          } else {
            notification.error({ message: "Error", description: `Member not found with ID: ${membershipId}` });
            navigate("/profiles");
          }
        } catch (error) {
          console.error("Error loading member:", error);
          notification.error({ message: "Error", description: `Failed to load member with ID: ${membershipId}` });
          navigate("/profiles");
        } finally {
          setLoading(false);
        }
      }
    };
    loadMember();
  }, [membershipId, selfServiceMode, isCreating]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset per-member check-in state when the loaded member changes, so a
  // check-in for one member doesn't suppress the button for the next member
  // selected within the same screen session.
  useEffect(() => {
    setCheckInTime(null);
    hasCheckedInRef.current = false;
  }, [membershipId]);

  // Auto-trigger check-in when conditions are met.
  useEffect(() => {
    if (shouldCheckIn && member && !checkInTime && member.isEnabled && membershipId && !hasCheckedInRef.current) {
      hasCheckedInRef.current = true;
      handleCheckIn();
    }
  }, [shouldCheckIn, member, checkInTime, membershipId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Per-member data once a member is loaded.
  useEffect(() => {
    if (member?.membershipId) {
      if (!selfServiceMode) {
        fetchPointOfSaleTransactions();
        fetchCancellations();
      }
      fetchDirectDebitPayments();
      fetchMemberPhoto();
      if (selfServiceMode) {
        fetchMyMandateState();
      }
      if (mode.health) {
        loadHealthSummary(member.membershipId);
      }
    }
  }, [member?.membershipId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPointOfSaleTransactions = async () => {
    if (!member?.membershipId) return;
    try {
      const transactions = await pointOfSaleService.getPointOfSaleTransactions(member.membershipId);
      setPointOfSaleTransactions(transactions);
    } catch (error) {
      console.error("Error fetching point of sale transactions:", error);
    }
  };

  const fetchCancellations = async () => {
    if (!member?.membershipId || selfServiceMode) return;
    try {
      const data = await memberService.getCancellations(member.membershipId);
      setCancellations(data ?? []);
    } catch (error) {
      console.error("Error fetching cancellations:", error);
      setCancellations([]);
    }
  };

  const fetchDirectDebitPayments = async () => {
    if (!member?.membershipId) return;
    try {
      const payments = selfServiceMode
        ? await paymentService.getMyPayments()
        : await paymentService.getAllPayments(member.membershipId);
      setMemberPayments(payments);
    } catch (error) {
      console.error("Error fetching direct debit payments:", error);
    }
  };

  // Self-service only: does the member already have a real direct-debit mandate?
  // The DD panel fetches this for its own rendering, but the completion banner needs
  // the answer at screen level (the panel lives behind a tab). Uses the throwing
  // variant so a failed fetch stays `null` ("we couldn't ask") rather than being
  // read as `false` ("no mandate") — telling a member with a good mandate to re-key
  // their bank details would be worse than saying nothing.
  const fetchMyMandateState = useCallback(async () => {
    try {
      const instruction = await paymentService.getMyDirectDebitInstructionOrThrow();
      // Only outstanding while the member can actually submit one themselves. A
      // mandate with real bank numbers isn't self-editable, so asking them for it
      // would hand them a form the server can only reject. A partial/zero-filled one
      // IS theirs to complete, so it stays outstanding and the prompt asks for it.
      setMandateSettled(instruction ? !isMandateSelfEditable(instruction.directDebitPayment) : false);
    } catch (error) {
      console.error("Error fetching own direct debit mandate:", error);
      setMandateSettled(null);
    }
  }, [paymentService]);

  const fetchMemberPhoto = async () => {
    if (!member?.membershipId) return;
    try {
      const photoUrl = selfServiceMode
        ? await memberService.getMyPhoto()
        : await memberService.getMemberPhoto(member.membershipId);
      setMember((prev) => (prev ? { ...prev, photoUrl } : undefined));
    } catch (error) {
      console.error("Error fetching member photo:", error);
    }
  };

  const loadHealthSummary = useCallback(
    async (id: string) => {
      setHealthLoading(true);
      setHealthLoadError(false);
      try {
        // A member loads their own summary via the /me endpoint; staff load any
        // member's by id.
        const summary = selfServiceMode
          ? await memberService.getMyHealthSummary()
          : await memberService.getHealthSummary(id);
        // The base client swallows GET failures and resolves to undefined rather
        // than throwing, so `catch` alone can't detect a failed fetch. Treat
        // undefined as a failure — otherwise a transient error reads as a
        // definitive "no questionnaire on file", which the completion banner would
        // present to the member as outstanding work (and invite a duplicate
        // submission).
        if (summary === undefined) {
          setHealthSummary(null);
          setHealthLoadError(true);
          return;
        }
        setHealthSummary(summary);
      } catch (error) {
        console.error("Error fetching health summary:", error);
        setHealthSummary(null);
        setHealthLoadError(true);
      } finally {
        setHealthLoading(false);
      }
    },
    [memberService, selfServiceMode],
  );

  // Warn on the Health tab (coloured dot + hover tooltip) when there's something
  // to action there: no questionnaire has been completed yet, or an answer is
  // flagged and not yet reviewed. The tooltip explains which, so staff/members
  // see it without opening the tab. Held back while the summary is still loading
  // (so an incomplete load doesn't flash a "missing" warning) and on a failed
  // fetch (so a transient API error isn't shown as a definitive "none on file").
  const healthWarning = useMemo((): { show: boolean; tooltip: string } => {
    if (!mode.health || healthLoading || healthLoadError) return { show: false, tooltip: "" };
    // An empty submission (submissionId set but no real answers) is not
    // "completed" either — staff still need to chase it.
    const hasSubmission = !!healthSummary && healthSummary.submissionId !== null
      && isHealthQuestionnairePopulated(healthSummary.answers);
    if (!hasSubmission) {
      return {
        show: true,
        tooltip: selfServiceMode
          ? "You haven't completed your health questionnaire yet."
          : "No health questionnaire has been completed for this member.",
      };
    }
    const flagged = computeFlaggedAnswers(healthSchema, healthSummary!.answers).length > 0;
    const reviewed = healthSummary!.reviews.length > 0;
    if (flagged && !reviewed) {
      return {
        show: true,
        tooltip: selfServiceMode
          ? "Some of your health answers may need a quick chat with the club."
          : "Flagged health answers are awaiting review.",
      };
    }
    return { show: false, tooltip: "" };
  }, [mode.health, healthLoading, healthLoadError, healthSummary, healthSchema, selfServiceMode]);

  const alerts = useMemo(() => {
    if (!member) return [];
    return computeAlerts(mode, {
      member,
      payments: memberPayments,
      pointOfSaleTransactions,
      cancellations,
      healthSummary,
      healthSchema,
      bannerRules,
    });
  }, [member, mode, memberPayments, pointOfSaleTransactions, cancellations, healthSummary, healthSchema, bannerRules]);

  // Which parts of the member's own profile are still outstanding. Only computed
  // for the self-service view of an existing member: staff have the full profile in
  // front of them and don't need a completion nudge, and the create/onboarding flow
  // is already a guided form. `null` for the health/DD inputs means "not resolved
  // yet", which keeps a section out of the list rather than reporting it missing off
  // half-loaded data.
  const completionSections = useMemo(() => {
    if (!member || !selfServiceMode || isCreating) return [];
    const healthComplete =
      healthLoading || healthLoadError
        ? null
        : !!healthSummary &&
          healthSummary.submissionId !== null &&
          isHealthQuestionnairePopulated(healthSummary.answers);
    return computeProfileCompletion({
      member,
      canEditName: mode.editName,
      directDebitComplete: mandateSettled,
      healthComplete,
      healthApplies: mode.health,
    });
  }, [member, selfServiceMode, isCreating, mode.editName, mode.health, mandateSettled, healthLoading, healthLoadError, healthSummary]);

  // Direct-debit mandate the member enters during onboarding (optional). Lifted
  // from the DirectDebitInput panel and submitted with the create request.
  const [onboardingDirectDebit, setOnboardingDirectDebit] = useState<
    { accountHolderName: string; bankName: string; accountNumber: string; sortCode: string } | null
  >(null);
  // Self-service onboarding: the member ticked "skip direct debit for now". Drops
  // the DD requirement from Save and omits the mandate from the create request.
  const [skipDirectDebit, setSkipDirectDebit] = useState(false);
  // FORT-456: a club that requires a Direct Debit never honours a skip, even one
  // ticked before the club config (re)loaded.
  const directDebitRequired = clientSettings.signup?.directDebitRequired ?? false;
  const effectiveSkipDirectDebit = skipDirectDebit && !directDebitRequired;
  // Whether the member has ticked to accept the club's direct-debit cancellation
  // terms. Only gates create when a notice is configured and a mandate is entered.
  const [directDebitAgreed, setDirectDebitAgreed] = useState(false);

  // Build a self-service create request from the merged blank member + form
  // values, plus the health answers collected in the mandatory pre-save prompt.
  // The server owns email / clientId / status / isEnabled / validFrom.
  const buildSelfServiceCreateRequest = (
    m: MemberProfile,
    healthAnswers: HealthAnswer[],
  ): SelfServiceCreateMemberRequest => {
    const dd = onboardingDirectDebit;
    // Only send a mandate when it's fully valid AND the member didn't opt to skip
    // it. A partial entry (or a skipped one) is omitted so the member can complete
    // direct debit after sign-up rather than hitting a backend error.
    const hasDirectDebit = !effectiveSkipDirectDebit && isDirectDebitComplete(dd);
    return {
      name: m.name,
      dateOfBirth: m.dateOfBirth,
      phoneNumber: m.phoneNumber,
      addressLine1: m.addressLine1,
      addressLine2: m.addressLine2,
      townCity: m.townCity,
      countyState: m.countyState,
      postZipCode: m.postZipCode,
      country: m.country,
      receiveEmails: m.specialOffers,
      membershipCategoryName: m.membershipCategory?.name ?? "",
      // Only send a mandate when the member filled all four fields; otherwise omit
      // it (direct debit is deferred to after sign-up). Server forces membershipId.
      directDebit: hasDirectDebit
        ? { membershipId: "", accountHolderName: dd!.accountHolderName, bankName: dd!.bankName, accountNumber: dd!.accountNumber, sortCode: dd!.sortCode, cancellationNoticeAgreed: directDebitAgreed }
        : null,
      metadata: m.metadata,
      // Health answers from the mandatory onboarding prompt. The server forces the
      // membershipId onto the submission once the member is created.
      healthForm: healthAnswers.length > 0 ? { answers: healthAnswers } : null,
    };
  };

  // Build an admin/front-of-house create request. The direct-debit mandate (if a
  // full one was entered on the create page) rides along; the health answers are
  // whatever staff filled in the inline questionnaire (optional for this path).
  // No membershipPaymentRequest — no placeholder payment rows on create.
  const buildAdminCreateRequest = (m: MemberProfile, healthAnswers: HealthAnswer[]): CreateMemberRequest => {
    const dd = onboardingDirectDebit;
    const hasDirectDebit = isDirectDebitComplete(dd);
    return {
      name: m.name,
      username: m.emailAddress,
      // Date-only, and the explicit sentinel when staff haven't entered one — the
      // same contract guest signup and the /newmember Quick Save use.
      dateOfBirth: toDateOnly(m.dateOfBirth) ?? DATE_OF_BIRTH_NOT_PROVIDED,
      emailAddress: m.emailAddress,
      addressLine1: m.addressLine1,
      addressLine2: m.addressLine2,
      townCity: m.townCity,
      countyState: m.countyState,
      postZipCode: m.postZipCode,
      country: m.country,
      phoneNumber: m.phoneNumber,
      receiveEmails: m.specialOffers,
      // Blank "Create User" always produces a not-active Inactive member; activation
      // (isEnabled=true / Active) is the separate admin "Activate Member" action
      // (FORT-72). Hardcode both so status and isEnabled can't disagree.
      status: "Inactive",
      // Category may be blank (staff can save without picking one) — the server
      // then falls back to the hidden "Default New Member" placeholder.
      membershipCategory: m.membershipCategory,
      // Staff-assigned member type (Member/Coach); the server defaults to Member
      // when blank.
      memberType: m.memberType,
      clientName: "",
      healthForm: { answers: healthAnswers },
      // Only send a mandate when all four fields are present; the server forces the
      // membershipId onto it. Omitted otherwise so no placeholder DD is created.
      directDebitRequest: hasDirectDebit
        ? { membershipId: "", accountHolderName: dd!.accountHolderName, bankName: dd!.bankName, accountNumber: dd!.accountNumber, sortCode: dd!.sortCode, cancellationNoticeAgreed: directDebitAgreed }
        : null,
      membershipPaymentRequest: null,
      validFrom: dayjs(m.validFrom).format("YYYY-MM-DD"),
      validTo: dayjs(m.validTo).format("YYYY-MM-DD"),
      isEnabled: false,
      creationType: "Default",
      metadata: m.metadata,
    };
  };

  // Create a brand-new member (onboarding = self-service; adminCreate = staff).
  // `values` are the merged form fields; falls back to the seeded blank member
  // for anything not edited. `healthAnswers` are the mandatory onboarding
  // questionnaire answers (empty for the admin create path). Returns whether the
  // create succeeded so the caller can keep the prompt open on failure.
  async function handleCreateMember(
    values: Partial<MemberProfile>,
    healthAnswers: HealthAnswer[] = [],
  ): Promise<boolean> {
    if (!member) return false;
    const merged: MemberProfile = { ...member, ...values };
    try {
      if (effectiveEntry === "onboarding") {
        await memberService.createMyProfile(buildSelfServiceCreateRequest(merged, healthAnswers));
        // The membership_id is written to Auth0 during create, so the token we
        // currently hold does NOT carry it. Force a fresh access token before
        // anything calls a /members/me/* endpoint — those read membership_id from
        // the JWT and would 403 (Forbid) on the stale token. auth/sync alone does
        // not refresh here: it only signals requiresTokenRefresh when IT writes to
        // Auth0, and by now the metadata is already written. Non-fatal: if the
        // refresh fails, the self-service load is resilient (it won't loop).
        try {
          // Causal refresh: must observe the membership_id this create just wrote
          // to Auth0, so bypass any in-flight (pre-write) refresh (FORT-293).
          await forceTokenRefresh(getAccessTokenSilently, { bypassInFlight: true });
        } catch (refreshError) {
          console.warn("Token refresh after create failed; continuing", refreshError);
        }
        // Re-sync so the app picks up the new membership id and drops into the
        // normal self-service view.
        await validateAuth();
        navigate("/my-profile", { replace: true });
      } else {
        const created = await memberService.create(buildAdminCreateRequest(merged, healthAnswers));
        navigate(`/profiles/${created.metadata?.LegacyMembershipId || created.membershipId}`, { replace: true });
      }
      return true;
    } catch (error) {
      notification.error({ message: "Error", description: "Failed to create member: " + error });
      return false;
    }
  }

  // Create a member from the personal-details Save, collecting the inline health
  // questionnaire answers. Self-service onboarding requires the questionnaire
  // (validate + block on missing required answers); the admin/front-of-house path
  // is optional (submit whatever's filled). The DD mandate is lifted separately
  // via onboardingDirectDebit and folded into the create request.
  const handleCreateFromForm = async (values: Partial<MemberProfile>) => {
    // Ignore repeat Saves while a create is already in flight (the button-disable
    // via `creatingMember` only takes effect on the next render).
    if (creatingRef.current) return;
    creatingRef.current = true;
    setCreatingMember(true);
    try {
      let healthAnswers: HealthAnswer[];
      if (effectiveEntry === "onboarding") {
        let healthValues: Record<string, unknown>;
        try {
          healthValues = await healthForm.validateFields();
        } catch {
          notification.warning({
            message: "Health questionnaire incomplete",
            description: "Please complete the required health questions below before saving.",
          });
          return; // required questions unanswered — the inline questionnaire shows the errors
        }
        healthAnswers = schemaToAnswers(healthSchema, healthValues);
      } else {
        // Optional for staff. Only submit a questionnaire if they actually filled
        // something in — an untouched form serialises to empty/false answers, and we
        // don't want to persist a spurious "empty" health submission.
        const rawHealth = healthForm.getFieldsValue();
        const hasHealthInput = Object.values(rawHealth).some(
          (v) => v !== undefined && v !== null && v !== "" && v !== false && !(Array.isArray(v) && v.length === 0),
        );
        healthAnswers = hasHealthInput ? schemaToAnswers(healthSchema, rawHealth) : [];
      }
      await handleCreateMember(values, healthAnswers);
    } finally {
      creatingRef.current = false;
      setCreatingMember(false);
    }
  };

  async function handleUpdateMember(values: Partial<MemberProfile>) {
    if (isCreating) {
      await handleCreateFromForm(values);
      return;
    }
    try {
      if (!member) return;
      const isExpiredCategory = values.membershipCategory?.name === "Expired";
      const today = new Date();
      const updatedValues = isExpiredCategory
        ? { ...values, isEnabled: false, status: "Inactive", validTo: today }
        : values;

      const updateRequest: UpdateMemberRequest = {
        ...member,
        ...updatedValues,
        dateOfBirth: updatedValues.dateOfBirth || member.dateOfBirth,
        validTo: toDateOnly(updatedValues.validTo) || toDateOnly(member.validTo),
        validFrom: toDateOnly(updatedValues.validFrom) || toDateOnly(member.validFrom),
        changeType: "Update",
      };

      if (selfServiceMode) {
        await memberService.updateMyProfile(updateRequest);
      } else {
        await memberService.update(updateRequest);
      }

      setMember((prev) => (prev ? { ...prev, ...updatedValues } : undefined));
      setSearchBarKey((prev) => prev + 1);
      notification.success({ message: "Success", description: "Member profile updated successfully" });
    } catch (error) {
      notification.error({ message: "Error", description: "Failed to update member profile: " + error });
    }
  }

  // Which wizard sections this visit has already persisted. The three saves are
  // separate, non-idempotent POSTs, and the direct-debit one is single-shot: once
  // real details exist the server 400s a member self-update ("already on file…
  // contact the club"). So if a later step fails, a retry must NOT re-send the
  // steps that already landed — that would dead-end the member on a step they can
  // never get past, and duplicate a health submission. Cleared when the wizard
  // reopens (a fresh set of sections).
  const savedSectionsRef = useRef<Set<string>>(new Set());

  // "Complete Profile" — persist every section the wizard collected, then refresh
  // so the top-of-profile banner recomputes (and disappears once nothing is left).
  // Each call suppresses its own success AND error toast (`showSuccess`/`showError`
  // false) so the member sees exactly one confirmation, and one friendly failure
  // message instead of that plus a raw API error. A failure re-throws so the wizard
  // stays open with their input, and whatever already saved is not re-sent.
  const handleCompleteProfile = useCallback(
    async (payload: CompleteProfilePayload) => {
      if (!member) return;
      const quiet = { showSuccess: false, showError: false } as const;
      const saved = savedSectionsRef.current;
      try {
        if (payload.personal && !saved.has("personal")) {
          const updateRequest: UpdateMemberRequest = {
            ...member,
            ...payload.personal,
            dateOfBirth: payload.personal.dateOfBirth || member.dateOfBirth,
            // The wizard sends only the keys it collects (today: EmergencyContact),
            // so MERGE onto the member's existing metadata. A plain spread would drop
            // GuestType / LeadSource / HowTheyHeard / Notes on the first completion.
            metadata: { ...member.metadata, ...payload.personal.metadata },
            validTo: toDateOnly(member.validTo),
            validFrom: toDateOnly(member.validFrom),
            changeType: "Update",
          };
          await memberService.updateMyProfile(updateRequest, quiet);
          saved.add("personal");
        }

        if (payload.directDebit && !saved.has("directDebit")) {
          await paymentService.updateMyDirectDebitInstruction(
            {
              membershipId: member.membershipId,
              accountHolderName: payload.directDebit.accountHolderName,
              bankName: payload.directDebit.bankName,
              accountNumber: payload.directDebit.accountNumber,
              // The wizard only accepts six bare digits, but strip any non-digits
              // defensively — the API expects the sort code unseparated.
              sortCode: payload.directDebit.sortCode.replace(/\D/g, ""),
              cancellationNoticeAgreed: payload.directDebit.cancellationNoticeAgreed,
            },
            quiet,
          );
          saved.add("directDebit");
        }

        if (payload.healthAnswers && payload.healthAnswers.length > 0 && !saved.has("health")) {
          await memberService.submitMyHealthForm(payload.healthAnswers, quiet);
          saved.add("health");
        }
      } catch (error) {
        notification.error({
          message: "Error",
          description: "We couldn't finish saving your profile. Please check your details and try again.",
        });
        // Surface the failure to the wizard so it keeps the member's input.
        throw error;
      }

      notification.success({
        message: "Successfully Completed Profile",
        description: "Thanks — we've saved your details.",
        duration: 6,
      });

      // Re-read everything the banner depends on so it clears itself.
      await Promise.all([
        refreshMyProfileQuietly(),
        fetchMyMandateState(),
        mode.health ? loadHealthSummary(member.membershipId) : Promise.resolve(),
      ]);
    },
    [member, memberService, paymentService, refreshMyProfileQuietly, fetchMyMandateState, loadHealthSummary, mode.health],
  );

  const handleSaveNote = async (notes: string) => {
    if (!member) return;
    setSavingNote(true);
    try {
      await handleUpdateMember({ metadata: { ...member.metadata, Notes: notes } });
    } finally {
      setSavingNote(false);
    }
  };

  const handleMemberSelect = async (selected: MemberProfile) => {
    // Clone before enriching so we don't mutate the object the search-results
    // list still holds (which would create hard-to-track shared-state bugs).
    let next = selected;
    if (!selected.photoUrl) {
      try {
        const photoUrl = await memberService.getMemberPhoto(selected.membershipId);
        next = { ...selected, photoUrl };
      } catch (error) {
        console.error("Error fetching member photo:", error);
      }
    }
    setMember(next);
    setLoading(false);
    navigate(`/profiles/${next.metadata?.LegacyMembershipId || next.membershipId}`, { replace: true });
  };

  const handleRefresh = () => {
    if (member?.membershipId) {
      setLoading(true);
      const fetchPromise = selfServiceMode ? memberService.getMyProfile() : memberService.getMember(member.membershipId);
      fetchPromise
        .then(async (memberProfile) => {
          if (memberProfile) {
            try {
              memberProfile.photoUrl = selfServiceMode
                ? await memberService.getMyPhoto()
                : await memberService.getMemberPhoto(memberProfile.membershipId);
            } catch (error) {
              console.error("Error fetching member photo:", error);
            }
            setMember(memberProfile);
          }
        })
        .catch(() => {
          notification.error({ message: "Error", description: "Failed to refresh member data" });
        })
        .finally(() => setLoading(false));
    } else if (!selfServiceMode) {
      setSearchBarKey((prev) => prev + 1);
    }
  };

  const handleCheckIn = async () => {
    if (!member) return;
    try {
      setCheckingIn(true);
      await memberService.checkInMember(member.membershipId);
      setCheckInTime(new Date());
      setCheckinRefreshKey((prev) => prev + 1);
      notification.success({
        message: "Success",
        description: `${member.name} has been checked in successfully!`,
        duration: 5,
      });

      // Birthday greeting if the client has enabled it and it's the member's birthday today.
      const greeting = birthdayGreeting(birthdayConfig, member);
      if (greeting) {
        notification.open({ message: "🎂 Birthday", description: greeting, duration: 8 });
      }
    } catch (error) {
      console.error("Check-in failed with error:", error);
      notification.error({ message: "Error", description: "Failed to check in member" });
    } finally {
      setCheckingIn(false);
    }
  };

  // The member search bar is a lookup affordance for the normal admin view; hide
  // it in self-service and in the blank create/onboarding flows.
  const searchBar = !selfServiceMode && !isCreating ? (
    <MemberSearchBar
      key={searchBarKey}
      onMemberSelect={handleMemberSelect}
      onSelect={() => {}}
      onRefresh={handleRefresh}
    />
  ) : null;

  if (loading || !member) {
    // Self-service load failed — show a retry, never an endless skeleton or a
    // redirect (which would loop with the completion redirect).
    if (selfServiceMode && selfLoadFailed && !loading) {
      return (
        <div data-testid="my-profile-load-error" style={{ maxWidth: 480, margin: "48px auto", padding: "0 16px" }}>
          <Alert
            type="error"
            showIcon
            message="We couldn't load your profile"
            description="This can happen briefly right after you sign up while your account finishes setting up. Please try again in a moment."
            action={
              <Button size="small" type="primary" data-testid="my-profile-retry" onClick={retrySelfLoad}>
                Retry
              </Button>
            }
          />
        </div>
      );
    }
    if (selfServiceMode || isCreating) return <Skeleton active />;
    return <Suspense fallback={<Skeleton active />}>{searchBar}</Suspense>;
  }

  const autoCheckInPending = shouldCheckIn && !hasCheckedInRef.current && !checkInTime;
  const checkInDisabled = !member.isEnabled || !!checkInTime || checkingIn || autoCheckInPending;
  const status = screenStatus(member, alerts);

  return (
    <ConfigProvider theme={fortiusProfileTheme}>
      <div className="ft-profile-v2" data-status={status} style={{ padding: "clamp(12px, 2vw, 24px)", minHeight: "100vh" }}>
        {searchBar}
        {/* Members with an incomplete profile (guest signups, abandoned onboarding)
            get a single prompt at the very top naming what's left, rather than having
            to discover it by clicking through the tabs. Self-hides once complete. */}
        {!completionDismissed && (
          <ProfileCompletionBanner
            sections={completionSections}
            onStart={() => setCompletionWizardOpen(true)}
            onDismiss={() => setCompletionDismissed(true)}
          />
        )}
        {completionSections.length > 0 && (
          <CompleteProfileWizard
            open={completionWizardOpen}
            sections={completionSections}
            member={member}
            mode={mode}
            healthSchema={healthSchema}
            cancellationNotice={cancellationNotice}
            onClose={() => setCompletionWizardOpen(false)}
            onOpened={() => savedSectionsRef.current.clear()}
            onSave={handleCompleteProfile}
          />
        )}
        {isCreating ? (
          // The stripped-down "Create User" page — personal & contact details and
          // direct debit side by side, with the health questionnaire inline below.
          // Capped width so it doesn't stretch across a wide desktop; the full
          // profile (rail + tabs) is only for existing members.
          <div style={{ maxWidth: 1400, margin: "0 auto" }}>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={effectiveEntry === "onboarding" ? "Complete your details" : "Create a new member"}
              description={
                effectiveEntry === "onboarding"
                  ? "Fill in your details, direct debit and health questionnaire. Your account will be reviewed and activated by the club."
                  : "Fill in the member's details. Use Save once a name and email are entered — the rest can be added later."
              }
            />
            <CreateMemberLayout
              member={member}
              mode={mode}
              selfServiceMode={selfServiceMode}
              membershipCategories={membershipCategories}
              onUpdateMember={handleUpdateMember}
              onDirectDebitChange={setOnboardingDirectDebit}
              directDebitComplete={isDirectDebitComplete(onboardingDirectDebit)}
              skipDirectDebit={effectiveSkipDirectDebit}
              directDebitRequired={directDebitRequired}
              onSkipDirectDebitChange={setSkipDirectDebit}
              healthSchema={healthSchema}
              healthForm={healthForm}
              healthRequired={effectiveEntry === "onboarding"}
              creating={creatingMember}
              cancellationNotice={cancellationNotice}
              directDebitAgreed={directDebitAgreed}
              onDirectDebitAgreedChange={setDirectDebitAgreed}
            />
          </div>
        ) : (
          <LayoutTwoColumn
            member={member}
            mode={mode}
            selfServiceMode={selfServiceMode}
            isCreating={isCreating}
            membershipCategories={membershipCategories}
            metadataTypes={metadataTypes}
            memberPayments={memberPayments}
            pointOfSaleTransactions={pointOfSaleTransactions}
            alerts={alerts}
            healthSummary={healthSummary}
            healthSchema={healthSchema}
            healthLoading={healthLoading}
            healthCanReview={mode.healthReview}
            healthWarning={healthWarning}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            checkinRefreshKey={checkinRefreshKey}
            checkedInAt={checkInTime}
            onCheckIn={handleCheckIn}
            checkingIn={checkingIn}
            checkInDisabled={checkInDisabled}
            onUpdateMember={handleUpdateMember}
            onSaveNote={handleSaveNote}
            savingNote={savingNote}
            onMemberUpdated={handleRefresh}
            onPhotoUploaded={handleRefresh}
            onPosTransactionUpdated={fetchPointOfSaleTransactions}
            onHealthSummaryChanged={() => loadHealthSummary(member.membershipId)}
            onDirectDebitChange={setOnboardingDirectDebit}
            directDebitComplete={isDirectDebitComplete(onboardingDirectDebit)}
            cancellationNotice={cancellationNotice}
            onDirectDebitSaved={selfServiceMode ? fetchMyMandateState : undefined}
          />
        )}
      </div>
    </ConfigProvider>
  );
};

export default MemberProfileScreenV2;
